// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

import React, { useEffect, useRef } from 'react';
import './TemporalAlertCascade.css';

const TemporalAlertCascade = ({ alerts }) => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const width = canvas.width = 400;
    const height = canvas.height = 600;

    // Alert particles
    const particles = [];
    const maxParticles = 100;

    // Create particles from alerts
    if (alerts && alerts.length > 0) {
      alerts.slice(0, 30).forEach((alert, i) => {
        const color = alert.severity === 'CRITICAL' ? '#ff4444' :
                     alert.severity === 'WARNING' || alert.severity === 'CAUTION' ? '#ffaa00' :
                     '#00ff88';
        
        particles.push({
          x: width / 2 + (Math.random() - 0.5) * 100,
          y: (i / 30) * height,
          vx: (Math.random() - 0.5) * 2,
          vy: Math.random() * 0.5 + 0.5,
          size: Math.random() * 3 + 2,
          color: color,
          alpha: 1,
          trail: [],
          message: alert.message
        });
      });
    }

    // Add ambient particles
    for (let i = particles.length; i < maxParticles; i++) {
      particles.push({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * 1,
        vy: Math.random() * 0.3 + 0.2,
        size: Math.random() * 2 + 1,
        color: '#00ffff',
        alpha: Math.random() * 0.5 + 0.3,
        trail: []
      });
    }

    // Energy waves
    const waves = [];
    let waveTimer = 0;

    const animate = () => {
      // Fade effect for trails
      ctx.fillStyle = 'rgba(0, 0, 0, 0.1)';
      ctx.fillRect(0, 0, width, height);

      // Draw grid
      ctx.strokeStyle = 'rgba(0, 255, 255, 0.1)';
      ctx.lineWidth = 1;
      for (let i = 0; i < width; i += 20) {
        ctx.beginPath();
        ctx.moveTo(i, 0);
        ctx.lineTo(i, height);
        ctx.stroke();
      }
      for (let i = 0; i < height; i += 20) {
        ctx.beginPath();
        ctx.moveTo(0, i);
        ctx.lineTo(width, i);
        ctx.stroke();
      }

      // Update and draw particles
      particles.forEach(p => {
        // Update position
        p.x += p.vx;
        p.y += p.vy;

        // Wrap around
        if (p.x < 0) p.x = width;
        if (p.x > width) p.x = 0;
        if (p.y > height) p.y = 0;

        // Add to trail
        p.trail.push({ x: p.x, y: p.y });
        if (p.trail.length > 10) p.trail.shift();

        // Draw trail
        ctx.strokeStyle = p.color;
        ctx.lineWidth = 1;
        ctx.globalAlpha = p.alpha * 0.3;
        ctx.beginPath();
        p.trail.forEach((point, i) => {
          if (i === 0) ctx.moveTo(point.x, point.y);
          else ctx.lineTo(point.x, point.y);
        });
        ctx.stroke();

        // Draw particle
        ctx.globalAlpha = p.alpha;
        ctx.fillStyle = p.color;
        ctx.shadowBlur = 15;
        ctx.shadowColor = p.color;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fill();
        ctx.shadowBlur = 0;
      });

      // Create waves periodically
      waveTimer++;
      if (waveTimer > 60) {
        waves.push({
          y: 0,
          speed: 2,
          amplitude: 30,
          frequency: 0.02,
          phase: Math.random() * Math.PI * 2,
          color: ['#ff4444', '#ffaa00', '#00ff88', '#00ffff'][Math.floor(Math.random() * 4)],
          alpha: 0.8
        });
        waveTimer = 0;
      }

      // Update and draw waves
      waves.forEach((wave, index) => {
        wave.y += wave.speed;
        wave.alpha -= 0.005;

        if (wave.alpha <= 0) {
          waves.splice(index, 1);
          return;
        }

        ctx.globalAlpha = wave.alpha;
        ctx.strokeStyle = wave.color;
        ctx.lineWidth = 2;
        ctx.shadowBlur = 10;
        ctx.shadowColor = wave.color;
        ctx.beginPath();
        
        for (let x = 0; x <= width; x += 5) {
          const y = wave.y + Math.sin(x * wave.frequency + wave.phase) * wave.amplitude;
          if (x === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        }
        ctx.stroke();
        ctx.shadowBlur = 0;
      });

      ctx.globalAlpha = 1;
      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [alerts]);

  return (
    <div className="temporal-alert-cascade">
      <canvas ref={canvasRef} width={400} height={600} />
      <div className="cascade-overlay">
        <div className="cascade-title">TEMPORAL ALERT CASCADE</div>
      </div>
    </div>
  );
};

export default TemporalAlertCascade;
